import { Vector3 } from "three";

/**
 * Glow layer configuration: [widthMultiplier, opacity, isCore]
 * - widthMultiplier: Width as a fraction of arc length (e.g., 0.1 = 10% of arc length)
 * - opacity: Base opacity for this layer (0-1)
 * - isCore: If true, renders as white; otherwise uses the configured glow color
 */
export type GlowLayer = [number, number, boolean];

/**
 * Configuration for the arc animation behavior
 */
export interface ArcAnimationConfig {
  /** How fast horizontal changes ease (0-1, higher = faster) */
  horizontalEasing: number;
  /** How fast vertical changes ease (0-1, higher = faster) */
  verticalEasing: number;
  /** Chance per point per frame to pick a new target position (0-1) */
  targetChangeChance: number;
  /** Maximum horizontal displacement range (0-1, 1 = full freedom) */
  horizontalRange: number;
  /** Maximum vertical displacement range (0-1) */
  verticalRange: number;
  /** How jagged/displaced the arc appears (higher = more displacement) */
  jaggedness: number;
  /** Minimum number of control points for arc shape */
  segmentsMin: number;
  /** Random additional segments on top of segmentsMin */
  segmentsRange: number;
  /** Number of points for the smooth curve rendering */
  curveSegments: number;
}

/**
 * Configuration for arc spawning and lifecycle (used by auto-spawning arcs)
 */
export interface ArcSpawnConfig {
  /** Maximum number of concurrent arcs */
  maxArcs: number;
  /** [min, max] interval between arc spawns in seconds */
  spawnInterval: [number, number];
  /** [min, max] lifetime of each arc in seconds */
  lifetime: [number, number];
  /** Time in seconds the scene must be "settled" before spawning */
  settlementDelay: number;
  /** Speed of the sine wave intensity modulation */
  sineSpeed: number;
}

/**
 * Visual styling configuration for arcs
 */
export interface ArcStyleConfig {
  /** Glow layers for desktop rendering */
  glowLayers: GlowLayer[];
  /** Glow layers for mobile rendering (typically fewer/simpler) */
  glowLayersMobile: GlowLayer[];
  /** RGB color for the glow effect (0-1 range) */
  glowColor: { r: number; g: number; b: number };
  /** RGB color for the core (typically white) */
  coreColor: { r: number; g: number; b: number };
}

/**
 * Full configuration for an Arcs instance
 */
export interface ArcsConfig {
  animation: ArcAnimationConfig;
  spawn: ArcSpawnConfig;
  style: ArcStyleConfig;
}

/**
 * Internal state for a single arc
 */
export interface ArcState {
  startPoint: Vector3;
  endPoint: Vector3;
  progress: number;
  lifetime: number;
  baseIntensity: number;
  // Smooth dancing state arrays
  horizontalCurrent: number[];
  horizontalTarget: number[];
  verticalCurrent: number[];
  verticalTarget: number[];
}

/**
 * Data for spawning a new arc manually
 */
export interface ArcSpawnData {
  startPoint: Vector3;
  endPoint: Vector3;
  lifetime?: number;
  intensity?: number;
}

/**
 * Default configuration values
 */
export const DEFAULT_ANIMATION_CONFIG: ArcAnimationConfig = {
  horizontalEasing: 0.12,
  verticalEasing: 0.04,
  targetChangeChance: 0.06,
  horizontalRange: 1.0,
  verticalRange: 0.6,
  jaggedness: 60,
  segmentsMin: 30,
  segmentsRange: 10,
  curveSegments: 40,
};

export const DEFAULT_SPAWN_CONFIG: ArcSpawnConfig = {
  maxArcs: 8,
  spawnInterval: [0.25, 0.55],
  lifetime: [0.25, 0.6],
  settlementDelay: 0.3,
  sineSpeed: 0.8,
};

export const DEFAULT_STYLE_CONFIG: ArcStyleConfig = {
  glowLayers: [
    [0.2, 0.1, false],
    [0.15, 0.15, false],
    [0.1, 0.22, false],
    [0.06, 0.32, false],
    [0.035, 0.45, false],
    [0.018, 0.65, false],
    [0.008, 1.0, true],
  ],
  glowLayersMobile: [
    [0.1, 0.2, false],
    [0.05, 0.4, false],
    [0.025, 0.6, false],
    [0.008, 1.0, true],
  ],
  glowColor: { r: 0.48, g: 0.84, b: 0.94 }, // #7ad7f0
  coreColor: { r: 1.0, g: 1.0, b: 1.0 },
};

export const DEFAULT_CONFIG: ArcsConfig = {
  animation: DEFAULT_ANIMATION_CONFIG,
  spawn: DEFAULT_SPAWN_CONFIG,
  style: DEFAULT_STYLE_CONFIG,
};
